<template>
  <div
    :class="{
      'lc-section': true,
      'lc-section-main': section.isMain,
      'lc-section-base': !section.isMain
    }"
  >
    <div v-if="!section.isMain">
      <ImgixImage
        v-if="section.speaker && section.speaker.photo"
        :image="section.speaker.photo"
        :width="40"
        :height="40"
        class="lc-section-photo"
        :alt="section.speaker.name"
      />

      <div v-else class="lc-section-initials">
        {{ initials }}
      </div>
    </div>

    <div class="self-center">
      <div class="lc-section-title-row">
        <h3 class="lc-section-title">
          {{ title }}
        </h3>

        <Badge v-if="isCurrent" class="self-start">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="24"
            viewBox="0 0 24 24"
            width="24"
          >
            <path d="M0 0h24v24H0z" fill="none" />
            <path
              d="M10 16.5l6-4.5-6-4.5v9zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
            />
          </svg>

          On
        </Badge>
      </div>

      <div class="lc-section-subtitle">
        {{ subtitle }}
      </div>
    </div>

    <div v-if="hasActions" class="lc-section-actions">
      <button
        v-if="canSeek"
        @click="$emit('seek-to', section.start)"
        type="button"
        class="lc-section-btn"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M13.8 12.9999C13.8 13.3313 13.5314 13.5999 13.2 13.5999C12.8687 13.5999 12.6 13.3313 12.6 12.9999V2.9999C12.6 2.66853 12.8687 2.3999 13.2 2.3999C13.5314 2.3999 13.8 2.66853 13.8 2.9999V12.9999Z"
            fill="white"
          />
          <path
            d="M2.60003 12.479C2.60003 13.2777 3.49019 13.7541 4.15475 13.3111L10.8731 8.83218C11.4668 8.43634 11.4668 7.56387 10.8731 7.16805L4.15475 2.68916C3.49019 2.24613 2.60003 2.72251 2.60003 3.52121V12.479Z"
            fill="white"
          />
        </svg>
      </button>

      <button
        v-if="hasSpeakerInfo"
        @click="isSpeakerInfoVisible = !isSpeakerInfoVisible"
        type="button"
        class="lc-section-btn"
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 1.999c5.524 0 10.002 4.478 10.002 10.002 0 5.523-4.478 10.001-10.002 10.001C6.476 22.002 2 17.524 2 12.001 1.999 6.477 6.476 1.999 12 1.999zm0 1.5a8.502 8.502 0 100 17.003A8.502 8.502 0 0012 3.5zm-.004 7a.75.75 0 01.744.648l.007.102.004 5.502a.75.75 0 01-1.494.102l-.006-.101-.004-5.502a.75.75 0 01.75-.75zm.005-3.497a.999.999 0 110 1.997.999.999 0 010-1.997z"
          />
        </svg>
      </button>
    </div>

    <div
      v-if="isSpeakerInfoVisible"
      class="lc-speaker-info col-span-2 content text-sm p-4 border-0 border-t border-solid border-gray-200"
      v-html="section.speaker.info"
    ></div>
  </div>
</template>

<script>
import ImgixImage from './ImgixImage';
import Badge from './Badge';
import SectionMixin from '../mixins/section';
import isCurrent from '../helpers/is-current';
import isEmpty from '../helpers/is-empty';

export default {
  components: { Badge, ImgixImage },
  mixins: [SectionMixin],

  props: { section: Object, currentTime: Number },

  data() {
    return { isSpeakerInfoVisible: false };
  },

  computed: {
    isCurrent() {
      return isCurrent(this.section, this.currentTime);
    },

    hasActions() {
      return this.canSeek || this.hasSpeakerInfo;
    },

    hasSpeakerInfo() {
      return this.section.speaker && !isEmpty(this.section.speaker.info);
    },

    canSeek() {
      return !isEmpty(this.section.start);
    }
  }
};
</script>

<style lang="scss">
@import '../styles/variables';

.lc-section {
  position: relative;
  display: grid;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
  text-align: left;

  + .lc-section-main {
    // Using padding improves the drag sorting visually
    padding-top: 1.5rem;

    .lc-section-actions {
      padding-top: 1.5rem;
    }
  }

  &:hover {
    .lc-section-actions {
      opacity: 1;
      transform: translateX(0);
    }
    .lc-badge {
      opacity: 0;
    }
  }
}

.lc-section-base {
  grid-template-columns: 2.5rem 1fr;
  padding: 0.75rem 1rem;
  background: $white-15;
  line-height: $leading-tight;
  border-radius: $border-radius-xs;

  .lc-section-title {
    color: $white;
    font-size: 1rem;
  }

  .lc-section-actions {
    top: 11px;
  }
}

.lc-section-main {
  grid-template-columns: 1fr;
  padding-right: 1rem;
  margin-bottom: 0.75rem;
  min-height: 1.375rem;
  line-height: $leading-tight;

  .lc-section-title {
    color: $white-70;
    font-weight: 800;
  }
}

.lc-section-title-row {
  display: grid;
  grid-template-columns: 1fr minmax(3rem, max-content);
  align-items: center;
  gap: 0.25rem;
  min-height: 1.5rem;
  line-height: 1.25rem;
}

.lc-section-actions {
  transition: opacity 0.2s $ease-in, transform 0.2s $ease-in;
  opacity: 0;
  transform: translateX(0.25rem);

  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: min-content;
  align-items: center;
  align-self: flex-start;
  justify-content: flex-end;
  gap: 0.5rem;
  position: absolute;
  right: 0.5rem;
  top: -1px;
  padding: 0 0.5rem;
  min-width: 4rem;
  /* background: $gray-100; */
  border-radius: 0.125rem;
  /* box-shadow: -0.25rem 0 0.25rem $gray-100; */
}

.lc-section-info {
  display: flex;
  align-items: center;
  align-self: flex-start;
  gap: 0.5rem;
  margin-left: auto;
}

.lc-section-title {
  margin: 0;
  padding-bottom: 0.125rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: $white;
}

.lc-section-subtitle {
  color: $white-70;
  font-size: 0.75rem;
  line-height: 1rem;
  font-weight: 500;
}

.lc-section-photo,
.lc-section-initials {
  align-self: flex-start;
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 5rem;
  background: rgba($primary, 0.25);
}

.lc-section-initials {
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
}

.lc-section-btn {
  transition: background-color $transition-duration $ease,
    border $transition-duration $ease, box-shadow $transition-duration $ease,
    color $transition-duration $ease;

  display: inline-flex;
  align-items: center;
  padding: 0.125rem;
  border: 0.125rem solid transparent;
  border-radius: 50%;
  color: $white;
  background-color: $white-15;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1.428571429;
  text-align: center;
  white-space: nowrap;

  vertical-align: middle;
  user-select: none;

  // Share hover and focus styles
  &:hover,
  &:focus {
    text-decoration: none;
  }

  &:focus,
  &.focus {
    outline: 0;
    box-shadow: $focus-shadow-btn;
  }

  svg {
    width: 1rem;
    height: 1rem;
    margin: 0;
    fill: $white;
  }
}

.lc-speaker-info {
  font-size: 0.75rem;
  color: $white;
  line-height: 1.6667;

  a {
    text-decoration: underline;
  }

  p {
    margin-top: 1rem;

    &:first-child {
      margin-top: 0;
    }
  }

  ul,
  ol {
    margin: 1rem 0;
    padding-left: 1.75rem;

    li + li {
      margin-top: 0.5rem;
    }
  }

  ul {
    list-style: circle;
  }

  ol {
    list-style: decimal;
  }
}
</style>
